/**
 * Push Notifications — Web Push (VAPID) via Supabase.
 *
 * Fluxo:
 *  1. App pede permissão de notificação ao usuário (Notification.requestPermission).
 *  2. Registra subscription no SW com `pushManager.subscribe(VAPID_PUBLIC_KEY)`.
 *  3. Salva endpoint + chaves criptográficas em `push_subscriptions` (Supabase).
 *  4. Backend (Edge Function "send-push") usa essas chaves pra enviar push.
 *
 * Pra ATIVAR completamente, o admin precisa:
 *  - Gerar VAPID keys: `npx web-push generate-vapid-keys`
 *  - Adicionar `VITE_VAPID_PUBLIC_KEY` no .env
 *  - Criar Edge Function `send-push` no Supabase com private key
 *  - Rodar SQL `push-subscriptions.sql` (tabela)
 *
 * Sem VAPID_PUBLIC_KEY configurado, `isPushSupported()` retorna false e a UI
 * esconde o toggle. Nada quebra.
 */
import { supabase } from '../supabase';

export type PushPermission = 'default' | 'granted' | 'denied' | 'unsupported';

const VAPID_PUBLIC_KEY =
  ((import.meta as { env?: { VITE_VAPID_PUBLIC_KEY?: string } }).env?.VITE_VAPID_PUBLIC_KEY as
    | string
    | undefined) || '';

// ─── Helpers ─────────────────────────────────────────────────────────────

/**
 * Converte VAPID public key (base64url) pra Uint8Array, formato esperado por
 * `pushManager.subscribe()`. Sem isso, Chrome reclama.
 */
function urlBase64ToUint8Array(base64String: string): Uint8Array {
  const padding = '='.repeat((4 - (base64String.length % 4)) % 4);
  const base64 = (base64String + padding).replace(/-/g, '+').replace(/_/g, '/');
  const rawData = atob(base64);
  const outputArray = new Uint8Array(rawData.length);
  for (let i = 0; i < rawData.length; i += 1) {
    outputArray[i] = rawData.charCodeAt(i);
  }
  return outputArray;
}

/** Lê o estado atual da Notification API (default / granted / denied). */
export function getPushPermission(): PushPermission {
  if (typeof window === 'undefined') return 'unsupported';
  if (!('Notification' in window)) return 'unsupported';
  return Notification.permission as PushPermission;
}

/**
 * Verifica se o ambiente suporta push notifications:
 *  - Browser tem `Notification`
 *  - Browser tem `serviceWorker` + `PushManager`
 *  - VAPID public key configurada
 */
export async function isPushSupported(): Promise<boolean> {
  if (typeof window === 'undefined') return false;
  if (!('Notification' in window)) return false;
  if (!('serviceWorker' in navigator)) return false;
  if (!('PushManager' in window)) return false;
  if (!VAPID_PUBLIC_KEY) {
    console.info('[push] VAPID public key não configurada — push desabilitado.');
    return false;
  }
  return true;
}

// ─── Subscribe / Unsubscribe ─────────────────────────────────────────────

/**
 * Pede permissão + cria subscription + persiste no Supabase.
 * Throws se falhar em qualquer etapa (UI mostra toast de erro).
 */
export async function enablePushForUser(userId: string): Promise<void> {
  if (!VAPID_PUBLIC_KEY) {
    throw new Error('Push notifications não configurado no app.');
  }

  // 1. Permission
  const permission = await Notification.requestPermission();
  if (permission !== 'granted') {
    throw new Error('Permissão negada pra notificações.');
  }

  // 2. Service Worker ready
  const registration = await navigator.serviceWorker.ready;
  if (!registration.pushManager) {
    throw new Error('Push Manager não disponível neste navegador.');
  }

  // 3. Cria (ou pega existente) subscription
  let subscription = await registration.pushManager.getSubscription();
  if (!subscription) {
    subscription = await registration.pushManager.subscribe({
      userVisibleOnly: true,
      applicationServerKey: urlBase64ToUint8Array(VAPID_PUBLIC_KEY),
    });
  }

  // 4. Persiste no Supabase
  const json = subscription.toJSON();
  const { error } = await supabase.from('push_subscriptions').upsert(
    {
      user_id: userId,
      endpoint: json.endpoint,
      p256dh: json.keys?.p256dh,
      auth: json.keys?.auth,
      user_agent: typeof navigator !== 'undefined' ? navigator.userAgent : null,
    },
    { onConflict: 'endpoint' },
  );
  if (error) {
    console.error('[push] erro ao salvar subscription', error);
    throw new Error('Não foi possível registrar suas notificações agora.');
  }
}

/**
 * Cancela subscription localmente e remove do Supabase.
 */
export async function disablePushForUser(userId: string): Promise<void> {
  try {
    const registration = await navigator.serviceWorker.ready;
    const subscription = await registration.pushManager.getSubscription();
    if (subscription) {
      const endpoint = subscription.endpoint;
      await subscription.unsubscribe();
      await supabase
        .from('push_subscriptions')
        .delete()
        .eq('endpoint', endpoint)
        .eq('user_id', userId);
    }
  } catch (err) {
    console.error('[push] erro ao desativar', err);
  }
}
